import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
} from "react";

import "./animated-list.css";
import { prefersReducedMotion, useAnimatedFrame } from "./use-animated-frame";

/**
 * React Bits "Animated List", recreated for the estate (FD-CAN-38; the
 * principal's words, 2026-09-02: "use the react bits animated list (but not
 * anamated, just the cards, and the scrolling and the effects when
 * scrolling)"). Plain React over one stylesheet — no motion dependency and
 * the vendor tree untouched (theme-adoption laws 2/3), the way ToolbarDynamic
 * beside it was recreated.
 *
 * Three effects survive from the catalog piece, every one scroll-driven and
 * none an entrance:
 *   (1) FADE EDGES that follow the scroll — the top fade rises with the
 *       first 48px of travel, the bottom fade leaves when nothing more
 *       scrolls (written straight to the elements; no render per scroll).
 *   (2) ROWS SETTLE IN as they enter the frame — an IntersectionObserver
 *       ratio written to `--vis`, which the stylesheet turns into opacity
 *       and a few percent of scale. Wherever the observer is unavailable
 *       (jsdom, old engines) `--vis` stays at its stylesheet default of 1
 *       and rows are simply visible.
 *   (3) SELECTION — hover selects; ArrowUp/Down/Home/End travel the
 *       selection WITHIN THE FOCUSED FRAME (never a window listener — the
 *       composer keeps its keys) and Enter runs the row's primary verb.
 *
 * The catalog's auto-add feed and its mount animation are deliberately
 * absent. prefers-reduced-motion stills the settle and the smooth scroll
 * (animated-list.css).
 *
 * The item SET is the list's identity, not the array: a refresh that keeps
 * any row keeps the scroll and the selection (by key); only a set with no
 * row in common starts at the top with nothing selected.
 *
 * RL-CAN-34: effects (1) and (2) are `useAnimatedFrame`'s, exported for any
 * scroller; this component keeps the frame's markup and effect (3).
 */
export interface AnimatedListProps<T> {
  readonly items: ReadonlyArray<T>;
  /** Stable identity per row — a uri, an id. */
  readonly keyOf: (item: T, index: number) => string;
  readonly renderItem: (item: T, index: number, state: { readonly selected: boolean }) => ReactNode;
  /** Enter on the selected row, or a double-click: the row's primary verb. */
  readonly onActivate?: ((item: T, index: number) => void) | undefined;
  /** The fade edges (default on). */
  readonly showGradients?: boolean;
  /** Fade edge height in px (default 56). */
  readonly fadeEdgeSize?: number;
  /** Arrow-key travel inside the focused frame (default on). */
  readonly enableArrowNavigation?: boolean;
  /** Show a thin scrollbar instead of hiding it (default hidden). */
  readonly displayScrollbar?: boolean;
  /** The settle-in effect (default on). */
  readonly settle?: boolean;
  readonly className?: string;
  readonly itemClassName?: string;
  /** Accessible name for the scroll frame. */
  readonly ariaLabel?: string | undefined;
  /** The frame's `data-cleo` hook. */
  readonly cleo?: string | undefined;
  /** Rendered in the frame when there are no items. */
  readonly emptyState?: ReactNode;
}

const FADE_DISTANCE_PX = 48;

export function AnimatedList<T>({
  items,
  keyOf,
  renderItem,
  onActivate,
  showGradients = true,
  fadeEdgeSize = 56,
  enableArrowNavigation = true,
  displayScrollbar = false,
  settle = true,
  className = "",
  itemClassName = "",
  ariaLabel,
  cleo,
  emptyState = null,
}: AnimatedListProps<T>): ReactElement {
  const scrollerRef = useRef<HTMLDivElement>(null);
  // The row keys, joined: the identity of the item SET, as opposed to the
  // identity of the array. A poll that hands the list a fresh array of the
  // same rows (the capacity list re-reads every minute) is not a new list.
  const keys = items.map((item, index) => keyOf(item, index));
  const signature = keys.join("\u0001");
  // RL-CAN-34: the fades and the settle ride the one frame hook, re-observing
  // on the set — never on an array identity a refresh happens to mint.
  const frame = useAnimatedFrame(scrollerRef, { settle, rows: signature });
  const { topFadeRef, bottomFadeRef } = frame;
  const [selected, setSelected] = useState(-1);
  // The selection at event time: two keys inside one React batch (ArrowDown
  // then Enter) must see each other, so the handlers read and write this
  // mirror and the state only paints it.
  const selectedRef = useRef(-1);
  // Only keyboard travel scrolls the selection into the frame; a hover
  // never moves the scroll under the pointer.
  const travelledByKeyRef = useRef(false);
  const select = useCallback((index: number) => {
    selectedRef.current = index;
    setSelected(index);
  }, []);

  // A genuinely new item set — no key in common with the last one — starts
  // at the top with nothing selected. A refreshed, reordered or filtered
  // set that keeps any row keeps the person's scroll where it is and keeps
  // the selection on the same row by key (dated fix 2026-09-14: every poll
  // of the rail's capacity list used to throw the scroll back to the top).
  // (Effect deps hold only the set's signature — never a callback or an
  // array identity — so the compiled deps array keeps one shape.)
  const previousKeysRef = useRef<ReadonlyArray<string> | null>(null);
  useLayoutEffect(() => {
    const previous = previousKeysRef.current;
    previousKeysRef.current = keys;
    if (previous !== null) {
      const survivors = new Set(previous);
      if (!keys.some((key) => survivors.has(key))) {
        const scroller = scrollerRef.current;
        if (scroller !== null) scroller.scrollTop = 0;
        select(-1);
      } else if (selectedRef.current >= 0) {
        const selectedKey = previous[selectedRef.current];
        const next = selectedKey === undefined ? -1 : keys.indexOf(selectedKey);
        if (next !== selectedRef.current) select(next);
      }
    }
    frame.paint();
    // The frame hook repaints on scroll and resize and settles the rows;
    // the deps hold only the set's signature so the compiled array keeps one shape.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // (3) Keyboard travel keeps the selection inside the frame, a fade's
  // height clear of either edge.
  useEffect(() => {
    if (!travelledByKeyRef.current || selected < 0) return;
    travelledByKeyRef.current = false;
    const scroller = scrollerRef.current;
    const row = scroller?.querySelector<HTMLElement>(`[data-alist-index="${selected}"]`);
    if (!scroller || !row) return;
    const margin = FADE_DISTANCE_PX;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    let target: number | null = null;
    if (top < scroller.scrollTop + margin) target = top - margin;
    else if (bottom > scroller.scrollTop + scroller.clientHeight - margin) {
      target = bottom - scroller.clientHeight + margin;
    }
    if (target === null) return;
    if (typeof scroller.scrollTo === "function") {
      scroller.scrollTo({ top: target, behavior: prefersReducedMotion() ? "auto" : "smooth" });
    } else {
      scroller.scrollTop = target;
    }
  }, [selected]);

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!enableArrowNavigation || items.length === 0) return;
    const target = event.target as HTMLElement;
    if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
      return;
    }
    const last = items.length - 1;
    const current = selectedRef.current;
    const travel = (next: number) => {
      event.preventDefault();
      travelledByKeyRef.current = true;
      select(Math.min(Math.max(next, 0), last));
    };
    switch (event.key) {
      case "ArrowDown":
        travel(current + 1);
        return;
      case "ArrowUp":
        travel(current <= 0 ? 0 : current - 1);
        return;
      case "Home":
        travel(0);
        return;
      case "End":
        travel(last);
        return;
      case "Enter": {
        // A row's own buttons answer their own Enter; the frame's Enter is
        // the selected row's primary verb.
        if (target !== event.currentTarget || current < 0 || onActivate === undefined) return;
        event.preventDefault();
        const item = items[current];
        if (item !== undefined) onActivate(item, current);
        return;
      }
      default:
        return;
    }
  };

  return (
    <div
      className={`ncl-alist ${className}`}
      data-alist=""
      data-cleo={cleo}
      style={{ "--alist-fade-size": `${fadeEdgeSize}px` } as CSSProperties}
    >
      <div
        aria-label={ariaLabel}
        className={`ncl-alist-scroller${displayScrollbar ? " ncl-alist-scrollbar" : ""}`}
        data-alist-scroller=""
        onKeyDown={onKeyDown}
        onScroll={() => frame.paint()}
        ref={scrollerRef}
        tabIndex={0}
      >
        {items.length === 0
          ? emptyState
          : items.map((item, index) => (
              <div
                className={`ncl-alist-item ${itemClassName}`}
                data-alist-index={index}
                data-alist-item=""
                data-selected={selected === index ? "true" : undefined}
                key={keyOf(item, index)}
                onDoubleClick={onActivate === undefined ? undefined : () => onActivate(item, index)}
                onPointerEnter={() => select(index)}
              >
                {renderItem(item, index, { selected: selected === index })}
              </div>
            ))}
      </div>
      {showGradients ? (
        <>
          <div aria-hidden="true" className="ncl-alist-fade ncl-alist-fade-top" ref={topFadeRef} />
          <div
            aria-hidden="true"
            className="ncl-alist-fade ncl-alist-fade-bottom"
            ref={bottomFadeRef}
          />
        </>
      ) : null}
    </div>
  );
}
